import capitalize from "@/utils/capitalize";
import { cn } from "@/utils/cn";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@babackapp/ui/components/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@babackapp/ui/components/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import {
  EyeClosedIcon,
  EyeOpenIcon,
  Pencil2Icon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { CheckIcon, ChevronDown, PlusIcon } from "lucide-react";
import { useReturnRequestsFilters } from "../../../providers/use-return-requests-filters";
import { useShopViews } from "../../hooks/use-shop-views";
import CreateShopViewDialog from "./create-shop-view-dialog";
import DeleteShopViewConfirmDialog from "./delete-shop-view-confirm-dialog";
import UpdateShopViewDialog from "./update-shop-view-dialog";

function ViewOptionsSelector() {
  const t = useTranslation();
  const { view: selectedView, setView } = useReturnRequestsFilters();

  if (!selectedView) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="flex border-l-0 rounded-l-none"
        >
          <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <UpdateShopViewDialog shopView={selectedView}>
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <Pencil2Icon className="mr-2 h-4 w-4" />
            {t("misc.edit")}
          </DropdownMenuItem>
        </UpdateShopViewDialog>
        <DeleteShopViewConfirmDialog
          shopView={selectedView}
          onDelete={() => setView(null)}
          disabled={selectedView.name === "default"}
        >
          <DropdownMenuItem
            onSelect={(e) => e.preventDefault()}
            disabled={selectedView.name === "default"}
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            {t("misc.delete")}
          </DropdownMenuItem>
        </DeleteShopViewConfirmDialog>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ShopViewSelector() {
  const t = useTranslation();
  const { view: selectedView, setView } = useReturnRequestsFilters();
  const views = useShopViews();

  return (
    <div className="flex">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn("flex", selectedView && "rounded-r-none")}
          >
            {selectedView ? (
              <EyeOpenIcon className="mr-2 h-4 w-4" />
            ) : (
              <EyeClosedIcon className="mr-2 h-4 w-4" />
            )}
            {selectedView ? capitalize(selectedView.name) : "Select view"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-56">
          <Command>
            <CommandInput
              className="border-0 focus:ring-0"
              onKeyDown={(e) => {
                // Prevent keyboard shortcut from the
                // filter button
                e.stopPropagation();
              }}
            />
            <CommandList>
              <CommandGroup title="Shop views">
                {views.map((view) => (
                  <CommandItem
                    key={view.id}
                    value={view.name}
                    onSelect={() => {
                      setView(view);
                    }}
                  >
                    <CheckIcon
                      className={cn(
                        "h-4 w-4",
                        view.id === selectedView?.id
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    {capitalize(view.name)}
                  </CommandItem>
                ))}
              </CommandGroup>
              <CommandSeparator />
              <CommandGroup forceMount>
                <CommandItem>
                  <CreateShopViewDialog>
                    <div className="flex items-center gap-2 w-full">
                      <PlusIcon className="h-4 w-4" />
                      {t("misc.add")}
                    </div>
                  </CreateShopViewDialog>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ViewOptionsSelector />
    </div>
  );
}
